import { useEffect, useRef } from 'react';

import {
  observeElementResize,
  syncCanvasBitmapSize,
} from '#/lib/observe-element-resize';

/**
 * Replicates the original CSS gradient + blur:
 *   radial-gradient(ellipse 1874px 882px at 50% 130%,
 *     #EFFDB7 0%, #BFBDFC 16.8%, #5F5BF7 32.2%, #1C1C1E 98.1%)
 *   filter: blur(24px)
 *
 * Rendered into a canvas once (and on resize) instead of using a live CSS
 * gradient + blur filter. Live gradients/filters get re-rasterized per tile
 * while the GSAP animation above runs, which causes visible seams/banding in
 * Firefox. A canvas is a static bitmap, so compositing it is deterministic.
 */
export const DesktopGradient = ({ className }: { className?: string }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let painted = false;
    const draw = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      const resized = syncCanvasBitmapSize(canvas);
      if (!resized && painted) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      painted = true;

      const blur = 24 * dpr;
      ctx.filter = `blur(${blur}px)`;

      // Overdraw beyond the canvas edges so the blur doesn't create a
      // transparent vignette at the borders
      const pad = blur * 3;
      ctx.fillStyle = '#1C1C1E';
      ctx.fillRect(-pad, -pad, w * dpr + pad * 2, h * dpr + pad * 2);

      const cx = (w / 2) * dpr;
      const cy = h * 1.3 * dpr;
      const rx = 1874 * dpr;
      const ry = 882 * dpr;

      // Canvas radial gradients are circular; scale the y axis to make the
      // ellipse, and draw the covering rect in that squashed space
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(1, ry / rx);
      const stretch = rx / ry;
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
      gradient.addColorStop(0, '#EFFDB7');
      gradient.addColorStop(0.168, '#BFBDFC');
      gradient.addColorStop(0.322, '#5F5BF7');
      gradient.addColorStop(0.981, '#1C1C1E');
      ctx.fillStyle = gradient;
      ctx.fillRect(
        -cx - pad,
        (-cy - pad) * stretch,
        w * dpr + pad * 2,
        (h * dpr + pad * 2) * stretch,
      );
      ctx.restore();
    };

    draw();
    return observeElementResize(canvas, draw);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{
        width: '100%',
        height: '100%',
        minWidth: '100vw',
      }}
    />
  );
};
